import React from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';

type Props = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  subtitle?: string;
  initialFocusRef?: React.RefObject<HTMLElement>;
  returnFocusRef?: React.RefObject<HTMLElement>;
  onEscClose?: () => void;
  regionId?: string; // for aria-labelledby
};

export default function InlinePanel({
  open,
  title,
  subtitle,
  onClose,
  children,
  className,
  initialFocusRef,
  returnFocusRef,
  onEscClose,
  regionId,
}: Props) {
  const shouldReduceMotion = useReducedMotion();
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const lastActiveRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (open) {
      lastActiveRef.current = document.activeElement as HTMLElement;
      // Focus first focusable element inside the panel after mount
      const id = window.setTimeout(() => {
        if (!panelRef.current) return;
        // Priority: provided initialFocusRef → first invalid → first input
        const target =
          initialFocusRef?.current ||
          panelRef.current.querySelector<HTMLElement>('[aria-invalid="true"], .input-error') ||
          panelRef.current.querySelector<HTMLElement>(
            'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
          );
        if (target) target.focus();
        else panelRef.current.focus();
      }, 0);
      return () => window.clearTimeout(id);
    } else if (!open && lastActiveRef.current) {
      // Restore focus to last active element when closing
      const id = window.setTimeout(() => {
        (returnFocusRef?.current ?? lastActiveRef.current)?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  // Close on ESC
  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (onEscClose) onEscClose();
        else onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', onKeyDown, { capture: true } as any);
  }, [open, onEscClose, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          key="inline-panel"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: shouldReduceMotion ? 0 : 0.15 }}
          className={`rounded-2xl border bg-background shadow-lg p-5 sm:p-6 ${className ?? ''}`}
          aria-live="polite"
          role="region"
          aria-labelledby={regionId}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 id={regionId} className="text-lg font-semibold">
                {title}
              </h2>
              {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
            </div>
            <button
              aria-label="Close"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div ref={panelRef as any} tabIndex={-1} className="mt-4">
            {children}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
